import React, { useEffect } from 'react';
import { Appearance } from '@atlaskit/button';
import { InvoiceWithLineItems } from '@atlassian/cssxp-types-billing';
import { AnalyticsEventSource, AnalyticsTrackSubject, trackButtonClick } from '@atlassian/temp-analytics';
import { ExportJSONTypes } from '@atlassian/temp-proxies';
import {
  MoreActionsItems,
  MoreActionsV2,
  useNotification,
  errorNotificationBuilder,
  NotificationTypes,
} from '@css-xp/base';
import { failureMessage } from '@css-xp/ppc-common';
import { useExportJSON } from '@css-xp/ppc-custom-hooks';
import { downloadInvoiceAsPdf } from '../../utils/invoice-download-helper';

export enum SIDEBAR {
  EDIT_SOLD_TO_ADDRESS = 'editSoldToAddress',
}

export const InvoiceMoreActions = ({
  invoice,
  transactionAccountId,
  isJSONAvailable,
  isVerifyJSONLoading,
  updateIsJsonDownloaded,
  appearance,
  trigger,
}: {
  invoice: InvoiceWithLineItems;
  transactionAccountId: string;
  isJSONAvailable?: boolean;
  isVerifyJSONLoading?: boolean;
  updateIsJsonDownloaded?: (_isJsonDownloaded: boolean) => void;
  appearance?: Appearance;
  trigger?: string;
}) => {
  const [downloadingInvoicePdf, setDownloadingInvoicePdf] = React.useState(false);
  const { downloadJSON, loading: loadingInvoiceJson, isCompleted } = useExportJSON({
    type: ExportJSONTypes.INVOICE,
    transactionAccountId,
    id: invoice?.id,
    isJSONAvailable,
    analyticsForDownloadedAction: {
      trackSubject: AnalyticsTrackSubject.INVOICE_JSON,
      attributes: {
        invoiceId: invoice?.id,
      },
    },
  });
  const { notificationSent, sendErrorNotification, setNotificationSent } = useNotification();
  const notifyError = (headerMsg: string) => {
    if (!notificationSent) {
      const notificationOptions = errorNotificationBuilder()
        .isVersion(NotificationTypes.FLAG)
        .withHeader(headerMsg)
        .withMessage(failureMessage())
        .withDismissAfter(5000)
        .build();
      sendErrorNotification(notificationOptions);
      setNotificationSent(true);
    }
  };

  const invoiceActions: MoreActionsItems[] = [
    {
      id: 'export-pdf-file',
      content: 'Export as PDF',
      action: async () => {
        setDownloadingInvoicePdf(true);
        trackButtonClick(AnalyticsTrackSubject.EXPORT_PDF, {
          source: AnalyticsEventSource.PARTNER_INVOICE_DETAILS_SCREEN,
        });
        try {
          await downloadInvoiceAsPdf(invoice, transactionAccountId);
        } catch (e) {
          notifyError(`We're unable to download the PDF file`);
        } finally {
          setDownloadingInvoicePdf(false);
        }
      },
    },
    {
      id: 'export-json-file',
      content: isJSONAvailable ? 'Export JSON' : 'Generate JSON',
      action: () => {
        downloadJSON();
      },
    },
  ];

  useEffect(() => {
    if (isCompleted && updateIsJsonDownloaded) {
      updateIsJsonDownloaded(true);
    }
  }, [isCompleted]);

  return (
    <div className="button-toolbar" data-testid="header-actions">
      <MoreActionsV2
        actions={invoiceActions}
        trigger={trigger || 'Export'}
        ariaLabel="menu"
        isLoading={loadingInvoiceJson || isVerifyJSONLoading || downloadingInvoicePdf}
        appearance={appearance}
      />
    </div>
  );
};
